import ClockoutController from '../../core/clockout.controller';
import Inherit from '../../core/inherit';
import { setAuthCookie } from '../../utils/cookies';
import LoginDelegate from './login.delegate';

/**
 * @extends ClockoutController
 * @extends LoginDelegate
 */
class LoginController extends Inherit(ClockoutController, LoginDelegate) {
    async initialize() {
        // Check if user has valid data (not just an empty object)
        if (this.user && this.user.access && this.user.access.length > 0) {
            this.navigateDashboard();
            return;
        }

        // Check if there's a token in the URL for invitation validation
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('token');
        
        if (token) {
            this.setState({ showResetPassword: true });
        }

        // Listen for password reset completion
        this.handlePasswordResetComplete = () => {
            this.setState({ showResetPassword: false });
        };
        window.addEventListener('passwordResetComplete', this.handlePasswordResetComplete);
    }

    async cleanup() {
        // Remove event listener when component unmounts
        if (this.handlePasswordResetComplete) {
            window.removeEventListener('passwordResetComplete', this.handlePasswordResetComplete);
        }
    }

    async authenticateUser() {
        const { email, password } = this.state;

        if (!email || !password) {
            this.setState({
                error: true,
                errorMessage: 'Please enter both your email and password.',
                loading: false,
            });
            return;
        }

        try {
            this.setState({ loading: true, error: false, errorMessage: '' });
            const response = await this.data.loginUser(this.state);

            if (response?.token) {
                setAuthCookie(response.token);
                // Load user data before navigating to ensure role is available for dashboard routing
                this.user = await this.data.fetchUserByToken();
                this.constructor._notifyAuthChange();
                this.navigateDashboard();
                return;
            }

            // data.loginUser swallows axios errors and returns {} on auth failure,
            // so we treat a missing token as a credentials problem.
            this.setState({
                error: true,
                errorMessage: this.resolveLoginErrorMessage(response),
            });
        } catch (error) {
            this.setState({
                error: true,
                errorMessage: this.resolveLoginErrorMessage(error?.response?.data),
            });
        } finally {
            this.setState({ loading: false });
        }
    }

    /**
     * Map known server payloads to user-friendly copy. Falls back to a generic
     * "incorrect credentials" message because the most common cause is a typo.
     * @param {object | undefined} payload
     * @returns {string}
     */
    resolveLoginErrorMessage(payload) {
        const raw = (payload?.error || payload?.message || '').toString().toLowerCase();
        if (raw.includes('locked')) {
            return 'Your account is locked. Please contact support.';
        }
        if (raw.includes('inactive') || raw.includes('disabled')) {
            return 'This account is no longer active.';
        }
        if (raw.includes('rate') || raw.includes('too many')) {
            return 'Too many attempts. Please wait a moment and try again.';
        }
        if (raw.includes('network') || raw.includes('timeout')) {
            return 'We couldn\u2019t reach the server. Check your connection and try again.';
        }
        return 'The email or password you entered is incorrect.';
    }
}

export default LoginController;
